// lib
import { useState, useMemo } from 'react'
import PropTypes from 'prop-types'

import {
  VECTORS_RAW,
  VECTORS_GROUPS,
  VECTORS_NAMES,
  VECTORS_ICONS,
  ADVISORY_LABEL,
  getVectorIcon,
  getVectorColor
} from '@aytm/app/stats/page/data-centrifuge/lib/vectors'

import useEventListener from '@aytm/app/stats/hooks/useEventListener'

// components
import Response from '@aytm/app/stats/page/data-centrifuge/components/Response'

// styles
import style from '@aytm/app/stats/page/data-centrifuge/styles/Responses.module.scss'

const suspicionLevels = [
  ['High', 4],
  ['Moderate', 3],
  ['Low', 2],
  ['Minor', 1]
]

// Static icon/title/color maps for every raw vector and its group children (port of
// responses.coffee:23-39). Child colors intentionally inherit the parent's color
// (getVectorColor(cur)). Vector-set is fixed, so this is computed once at module load.
const vectors = VECTORS_RAW.reduce(
  (acc, cur) => {
    acc.icons[cur] = VECTORS_ICONS[cur]
    acc.titles[cur] = VECTORS_NAMES[cur]
    acc.colors[cur] = getVectorColor(cur)

    const group = VECTORS_GROUPS.find(item => item.id === cur && 0 < item.vectors.length)
    if (group) {
      group.vectors.forEach((childId, i) => {
        acc.icons[childId] = getVectorIcon(childId)
        acc.titles[childId] = group.names[i]
        acc.colors[childId] = getVectorColor(cur)
      })
    }
    return acc
  },
  { icons: {}, titles: {}, colors: {} }
)
const Responses = ({ tier, model, settled = false, onRestore, onReject, onScrollToQuestion }) => {
  // Parent-owned review-target state. `tick` bumps on every `reviewResponse`
  // event so a Response whose `uid` matches `review.uid` can scroll again
  // even when the user clicks the same tooltip twice in a row. Avoids the
  // prior fire-once-via-state reset useEffect: we never clear the uid, we
  // just monotonically bump `tick`; Response cards watch the tick, not the
  // uid, for scroll-trigger semantics.
  const [review, setReview] = useState({ uid: null, tick: 0 })

  const suspicionLevelsUIDs = useMemo(
    () =>
      model.scores.reduce((acc, [uid, ...scores]) => {
        const level = suspicionLevels.find(l => l[1] <= model.suspicionScores[uid])
        if (level == undefined) return acc

        const status =
          Object.hasOwn(model.responses, uid) &&
          scores.some((v, i) => !model.disabledVectors.includes(model.vectors[i].id) && 0 < v) &&
          model.suspicionLevel <= model.suspicionScores[uid] &&
          ('all' == model.filter ||
            (!['dc-auto-rejected', 'dc-restoring'].includes(model.responses[uid].status) &&
              'quarantined' != model.filter) ||
            (['dc-auto-rejected', 'dc-restoring'].includes(model.responses[uid].status) && 'flagged' != model.filter))
        if (!status) return acc

        acc[level[1]] ||= []
        acc[level[1]].push(uid)

        return acc
      }, {}),
    [model]
  )

  useEventListener('reviewResponse', event => {
    // Keep the uid as dispatched — do NOT coerce with Number(). A flat stat-uid row is sanitized
    // to a non-numeric string id (calc-data.js), which Number() turns into NaN, so the strict
    // match in Response never fires and Review Evidence silently no-ops.
    setReview(prev => ({ uid: event.detail.uid, tick: prev.tick + 1 }))
  })

  // Advisory rows are not on the threshold scale, so they arrive pre-filtered rather than being
  // bucketed by level here.
  const advisoryUIDs = model.advisoryFiltered || []

  const empty = suspicionLevels.every(level => !suspicionLevelsUIDs[level[1]]?.length) && !advisoryUIDs.length

  return (
    <div data-testid="stat-dc-responses" className={style.component}>
      {/* A clean run is a result, not an absent surface — say so rather than collapsing to nothing. */}
      {empty && settled ? (
        <div data-testid="stat-dc-responses-empty" className={style.empty}>
          No flagged responses at the current suspicion threshold and filter.
        </div>
      ) : null}
      {suspicionLevels.map(level => {
        const items = suspicionLevelsUIDs[level[1]]
        return items != undefined && 0 < items.length ? (
          <div
            key={`level-${level[1]}`}
            data-testid={`stat-dc-responses-level-${level[0].toLowerCase()}`}
            className={style['suspicion-level-component']}
          >
            <div data-testid={`stat-dc-responses-level-header-${level[0].toLowerCase()}`} className={style.header}>
              Suspicion threshold:&nbsp;
              <b>
                {level[0]} ({items.length})
              </b>
            </div>
            {items.map(uid => (
              <Response
                key={uid}
                uid={uid}
                tier={tier}
                item={model.responses[uid]}
                vectors={vectors}
                review={review}
                onRestore={onRestore}
                onReject={onReject}
                onScrollToQuestion={onScrollToQuestion}
              />
            ))}
          </div>
        ) : null
      })}
      {0 < advisoryUIDs.length ? (
        <div data-testid="stat-dc-responses-level-advisory" className={style['suspicion-level-component']}>
          <div data-testid="stat-dc-responses-level-header-advisory" className={style.header}>
            Suspicion threshold:&nbsp;
            <b>
              {ADVISORY_LABEL} ({advisoryUIDs.length})
            </b>
          </div>
          {advisoryUIDs.map(uid => (
            <Response
              key={uid}
              uid={uid}
              tier={tier}
              item={model.responses[uid]}
              vectors={vectors}
              review={review}
              onRestore={onRestore}
              onReject={onReject}
              onScrollToQuestion={onScrollToQuestion}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

Responses.propTypes = {
  tier: PropTypes.string,
  model: PropTypes.object,
  settled: PropTypes.bool,
  onRestore: PropTypes.func,
  onReject: PropTypes.func,
  onScrollToQuestion: PropTypes.func
}

Responses.displayName = 'Responses'

export default Responses
